// React access to the kernel.
//
// §4.2 — engine code holds vanilla store handles; React subscribes, never the reverse. These
// hooks are the only place React and the kernel meet, and they live in `kernel/` so a skin can
// use them without importing `host/`.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import type { StoreApi } from 'zustand/vanilla';
import type { VfsPath, Win } from '@hangar/shared';
import type { HangarKernel } from './index';
import type { DesktopItem } from './desktop';
import type { SettingId, SettingValue } from './settings';
import type { ClipboardEntry } from './transfer/clipboard';
import type { Notification, NotificationGroup } from './notify';
import type { SearchEntry, SearchResultGroup } from './search';

const KernelContext = createContext<HangarKernel | null>(null);

export const KernelProvider = KernelContext.Provider;

export function useKernel(): HangarKernel {
  const kernel = useContext(KernelContext);
  if (!kernel) throw new Error('useKernel: no <KernelProvider> above this component');
  return kernel;
}

/** Subscribe to a vanilla Zustand store with a selector, without pulling in `zustand/react`. */
export function useStoreValue<S, T>(store: StoreApi<S>, select: (state: S) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.getState()),
    () => select(store.getState()),
  );
}

export function useSetting<K extends SettingId>(key: K): SettingValue<K> {
  const kernel = useKernel();
  return useStoreValue(kernel.settings.store, (s) => s[key]);
}

/** Every window, ascending by z. Recomputed only when the store changes. */
export function useWindows(): Win[] {
  const kernel = useKernel();
  const windows = useStoreValue(kernel.wm.store, (s) => s.windows);
  return Object.values(windows).sort((a, b) => a.z - b.z);
}

export function useWindow(id: string): Win | undefined {
  const kernel = useKernel();
  return useStoreValue(kernel.wm.store, (s) => s.windows[id]);
}

export function useFocusedWindowId(): string | null {
  const kernel = useKernel();
  return useStoreValue(kernel.wm.store, (s) => s.focusedId);
}

export function useActiveDesktopId(): string {
  const kernel = useKernel();
  return useStoreValue(kernel.wm.store, (s) => s.activeDesktopId);
}

/**
 * §9.6 — the installed apps currently dimmed. A skin renders `data-health` from this; the 50 % is
 * one rule in `shell.css`, because "dimmed" is the shell's statement about the app, not a look
 * either skin gets to disagree with.
 *
 * Not a `useSyncExternalStore`: the answer is a derived Set, and `getSnapshot` has to be
 * referentially stable across calls, which a freshly-built Set is not.
 */
export function useUnhealthyApps(): ReadonlySet<string> {
  const kernel = useKernel();
  const [unhealthy, setUnhealthy] = useState<ReadonlySet<string>>(() => unhealthyIn(kernel));

  useEffect(() => {
    const refresh = (): void =>
      setUnhealthy((prev) => {
        const next = unhealthyIn(kernel);
        // Same answer, same object: a fresh Set every registry event would re-render every bar
        // entry on every health check, three times a launch.
        if (prev.size === next.size && [...next].every((id) => prev.has(id))) return prev;
        return next;
      });
    refresh();
    return kernel.bus.on('app:registryChanged', refresh);
  }, [kernel]);

  return unhealthy;
}

/**
 * §10.1 — the clipboard ring, newest first.
 *
 * Not a `useSyncExternalStore` for the same reason as above: `history()` builds a fresh array each
 * call, and `getSnapshot` has to be referentially stable. The service announces on every change,
 * which is the only time this needs to re-read.
 */
export function useClipboardHistory(): readonly ClipboardEntry[] {
  const kernel = useKernel();
  const [history, setHistory] = useState<readonly ClipboardEntry[]>(() =>
    kernel.clipboard.history(),
  );

  useEffect(() => {
    const refresh = (): void => setHistory(kernel.clipboard.history());
    refresh();
    return kernel.clipboard.subscribe(refresh);
  }, [kernel]);

  return history;
}

/**
 * §12 — the search index, queried for a launcher.
 *
 * One hook for every skin's launcher, because §12's answers are the same in all of them: the same
 * groups, in the same order, with the same thing selected. What each skin does with them — a
 * centred palette, a Start menu — is the skin's.
 *
 * `flat` is the groups in display order, which is the order the arrow keys move through: a user
 * pressing Down twice expects the third row on screen, not the third result in some other order.
 */
export function useSearch(
  text: string,
  active: boolean,
): { groups: readonly SearchResultGroup[]; flat: readonly SearchEntry[] } {
  const kernel = useKernel();
  /** Bumped whenever the index changes, so an open launcher re-queries a stale answer. */
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!active) return;
    // The built-ins are registered *after* the kernel is constructed, and an app may have been
    // installed since the last time this opened — so the cheap sources are refreshed on open.
    kernel.reindexSearch();
    return kernel.search.subscribe(() => setRevision((n) => n + 1));
  }, [kernel, active]);

  const groups = useMemo(
    // `revision` is the dependency that matters and is deliberately unused in the body: it is how
    // an index change reaches a query whose text has not moved.
    () => (active ? kernel.search.query(text) : []),
    [kernel, active, text, revision],
  );
  const flat = useMemo(
    () => groups.flatMap((group) => group.results.map((result) => result.entry)),
    [groups],
  );

  return { groups, flat };
}

/**
 * §13 — the Notification Center, newest first, and the toasts currently up.
 *
 * The 6 s clock lives here rather than in each skin's `NotificationHost` because it is one rule
 * (§13) and there are three skins. The service says when the earliest toast is due; this sets one
 * timer for that moment and asks it to sweep, so a toast comes down when it is meant to rather
 * than at the granularity of a poll.
 */
export function useNotifications(): {
  center: readonly Notification[];
  toasts: readonly Notification[];
  groups: readonly NotificationGroup[];
} {
  const kernel = useKernel();
  const [snapshot, setSnapshot] = useState(() => readNotifications(kernel));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const refresh = (): void => {
      setSnapshot(readNotifications(kernel));
      clearTimeout(timer);
      const due = kernel.notify.nextExpiry();
      if (due === null) return;
      // `Math.max(0, …)` rather than trusting the difference: a tab that was backgrounded comes
      // back with expiries already in the past, and a negative delay is not a delay.
      timer = setTimeout(() => kernel.notify.sweep(Date.now()), Math.max(0, due - Date.now()));
    };

    refresh();
    const stop = kernel.notify.subscribe(refresh);
    return () => {
      clearTimeout(timer);
      stop();
    };
  }, [kernel]);

  return snapshot;
}

/**
 * §13 — "a notification badges its app's bar icon": how many Center entries each app has.
 *
 * Derived from the groups rather than asked per app, so a bar with eight entries makes one pass
 * instead of eight, and every skin's bar reads the same number.
 */
export function useNotificationBadges(): ReadonlyMap<string, number> {
  const { groups } = useNotifications();
  return useMemo(
    () => new Map(groups.map((group) => [group.appId, group.items.length])),
    [groups],
  );
}

function readNotifications(kernel: HangarKernel): {
  center: readonly Notification[];
  toasts: readonly Notification[];
  groups: readonly NotificationGroup[];
} {
  return {
    center: kernel.notify.center(),
    toasts: kernel.notify.toasts(),
    groups: kernel.notify.groups(),
  };
}

function unhealthyIn(kernel: HangarKernel): ReadonlySet<string> {
  return new Set(
    kernel.installed
      .list()
      .filter((app) => app.health === 'unhealthy')
      .map((app) => app.id),
  );
}

export function useDialog() {
  const kernel = useKernel();
  return useStoreValue(kernel.shellui.store, (s) => s.dialogs[0]);
}

/**
 * §11.7 (D-276) — what is on the desktop, or inside one of its folders.
 *
 * The listing is asynchronous because the desktop is a real directory, so this is `useState` +
 * `subscribe` rather than `useSyncExternalStore`: there is no synchronous snapshot to read. Reads
 * are sequenced by a generation counter, because a `watch` burst — the seed writes twenty
 * shortcuts — starts twenty overlapping listings and the last one to *resolve* is not necessarily
 * the last one to have been *asked for*.
 */
export function useDesktopItems(dir?: VfsPath): readonly DesktopItem[] {
  const kernel = useKernel();
  const [items, setItems] = useState<readonly DesktopItem[]>([]);

  useEffect(() => {
    let alive = true;
    let issued = 0;
    let applied = 0;

    const refresh = (): void => {
      const generation = (issued += 1);
      void kernel.desktop
        .list(dir)
        .then((next) => {
          if (!alive || generation < applied) return;
          applied = generation;
          setItems(next);
        })
        .catch(() => undefined);
    };

    refresh();
    const stop = kernel.desktop.subscribe(refresh);
    return () => {
      alive = false;
      stop();
    };
  }, [kernel, dir]);

  return items;
}
